import { Box } from '@mui/material';
import { fontStyles } from '@natomalabs/natomui/src/theme/fonts';
import React, { forwardRef, useId } from 'react';
import TextareaAutosize from '@mui/material/TextareaAutosize';

export type TextFieldProps = React.InputHTMLAttributes<
  HTMLInputElement | HTMLTextAreaElement
> & {
  /**
   * Label for the text field. Renders outside of the input.
   */
  label?: React.ReactNode;
  /**
   * The variant to use of the input.
   */
  variant?: 'normal' | 'monospace';
  /**
   * Whether to render a multi-line text area vs. a single line input element.
   */
  multiline?: boolean;
  /**
   * Start adornment for the text field. Renders inside the input.
   */
  startAdornment?: React.ReactNode;
  /**
   * End adornment for the text field. Renders inside the input.
   */
  endAdornment?: React.ReactNode;
  /**
   * If `true`, the `input` will indicate an error.
   * The prop defaults to the value (`false`) inherited from the parent FormControl component.
   */
  error?: boolean;
  /**
   * If `true`, the `input` will be required.
   * The prop defaults to the value (`false`) inherited from the parent FormControl component.
   */
  required?: boolean;
  /**
   * If `true`, the `input` will expand to be full width.
   */
  fullWidth?: boolean;
  /**
   * Helper text to display below the input.
   */
  helperText?: React.ReactNode;
  /**
   * Minimum number of rows for a multiline text field.
   */
  minRows?: number;
  /**
   * Maximum number of rows for a multiline text field.
   */
  maxRows?: number;
  'data-testid'?: string;
};

/**
 *
 * @param props text field props (mostly HTMLInputElement props)
 * @returns TextField component
 */
const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField(
    {
      label,
      multiline = false,
      error = false,
      required = false,
      fullWidth = false,
      variant = 'normal',
      helperText,
      startAdornment,
      minRows,
      maxRows,
      endAdornment,
      onBlur,
      ...props
    },
    ref
  ) {
    const id = useId();

    const inputProps = {
      'aria-invalid': !!error,
      'aria-labelledby': `${id}-label`,
      id,
      onBlur
    };

    const fontStyle =
      variant === 'monospace'
        ? { fontFamily: 'Monospace', fontSize: 12 }
        : fontStyles.textRegularNormal;

    return (
      <Box
        sx={(theme) => ({
          display: 'inline-flex',
          flexDirection: 'column',
          rowGap: theme.spacing(0.5),
          width: fullWidth ? '100%' : 'auto'
        })}
      >
        {label && (
          <Box
            component="label"
            id={`${id}-label`}
            htmlFor={id}
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'row',
              gap: theme.spacing(0.5),
              fontFamily: fontStyles.textMiniMedium,
              color: theme.palette.text.ternary,
              padding: theme.spacing(0.5)
            })}
          >
            {label}
            {required && !props.value && (
              <Box sx={(theme) => ({ color: theme.palette.error.main })}>*</Box>
            )}
          </Box>
        )}
        <Box
          className={props.disabled ? 'Natoma-disabled' : ''}
          sx={(theme) => ({
            display: 'inline-flex',
            alignItems: 'center',
            columnGap: theme.spacing(1),
            width: '100%',
            borderRadius: theme.spacing(1.5),
            padding: theme.spacing(0, 2),
            border: `1px solid ${error ? theme.palette.error.main : theme.palette.actionable.secondary.default.border}`,
            backgroundColor:
              theme.palette.actionable.secondary.default.background,
            ...(multiline && { overflowY: 'scroll' }),
            outlineWidth: 0,
            outlineStyle: 'solid',
            outlineColor: error
              ? theme.palette.actionable.danger.focusRing
              : theme.palette.actionable.secondary.focusRing,
            transition: 'outline-width 0.1s ease-out',
            '&:not(.Natoma-disabled):hover': {
              backgroundColor:
                theme.palette.actionable.secondary.active.background
            },
            '&:focus-within': {
              borderColor: error
                ? theme.palette.actionable.danger.active.border
                : theme.palette.actionable.secondary.active.border,
              outlineWidth: 4
            },
            '&.Natoma-disabled, &.Natoma-disabled input, &.Natoma-disabled textarea':
              { cursor: 'not-allowed' },
            '& input, & textarea': {
              ...fontStyle,
              fontVariantLigatures: 'none',
              border: 0,
              flexGrow: 1,
              outline: 'none',
              padding: theme.spacing(1.5, 0),
              margin: 0,
              minWidth: 0,
              color: theme.palette.text.secondary,
              backgroundColor: 'transparent',
              '&:focus': { outline: 'none' },
              '&::placeholder': { color: theme.palette.text.disabled }
            },
            '& textarea': {
              resize: 'none'
            },
            '& textarea::-webkit-resizer': { display: 'none' }
          })}
        >
          {startAdornment}
          {multiline ? (
            <TextareaAutosize
              {...props}
              {...inputProps}
              maxRows={maxRows}
              minRows={minRows}
            />
          ) : (
            <input ref={ref} {...props} {...inputProps} />
          )}
          {endAdornment}
        </Box>
        {helperText && (
          <Box
            sx={(theme) => ({
              padding: theme.spacing(0, 0.5),
              fontFamily: fontStyles.textMiniNormal,
              color: error ? theme.palette.error.main : 'inherit'
            })}
          >
            {helperText}
          </Box>
        )}
      </Box>
    );
  }
);

export default TextField;
